import React, { useEffect, useMemo, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { Link, Navigate } from 'react-router-dom';
import AuctionFilter from '../AuctionFilter/AuctionFilter';
import AuctionPreview from '../AuctionPreview/AuctionPreview';
import PaginationFull from '../Pagination/Pagination';
import PaginationSimple from '../Pagination/PaginationSimple';
import ProgressIndicator from '../ProgressIndicator/ProgressIndicator';
import { AUCTION_PROP_TYPE } from '../../utils/props';
import { getFilterParamsFromMatch } from '../../utils/auctions';
import {
    getFeature,
    FEATURE__SHOWCASE
} from '../../utils/features';

import './AuctionsList.css';

const PaginationWrapper = memo((props) => getFeature(FEATURE__SHOWCASE)
    ? <PaginationSimple {...props} />
    : <PaginationFull {...props} />);
PaginationWrapper.displayName = 'PaginationWrapper';

const AuctionsList = ({
    loadData,
    prefetchPage,
    auctions,
    match,
    Filter = AuctionFilter,
    auctionsLoading,
    categoryName,
    auctionsPerPage,
    past,
    getUrl,
    lockCategory,
    totalPages,
    totalItems,
    premiumFilter,
}) => {
    const { page, category, region, date } = getFilterParamsFromMatch(match, lockCategory);
    const showcase = getFeature(FEATURE__SHOWCASE);
    const pageNumber = useMemo(() => page && parseInt(page, 10), [page]);

    // Load data on mount and when filters change
    useEffect(() => {
        if (loadData) {
            loadData();
        }
    }, [page, category, region, date, premiumFilter]); // loadData intentionally excluded - recreated each render

    // Prefetch adjacent pages when current page loads
    useEffect(() => {
        if (!auctionsLoading && auctions && auctions.length > 0 && prefetchPage) {
            const current = pageNumber || 1;

            // Prefetch previous page (for back navigation)
            if (current > 1) {
                prefetchPage(current - 1, category, region, date, past);
            }

            // Prefetch next 2 pages (for forward navigation)
            const hasMorePages = !totalPages || current < totalPages;
            if (hasMorePages) {
                prefetchPage(current + 1, category, region, date, past);
                // Prefetch one more page ahead
                if (!totalPages || current + 1 < totalPages) {
                    prefetchPage(current + 2, category, region, date, past);
                }
            }
        }
    }, [auctionsLoading, auctions, pageNumber, prefetchPage, category, region, date, past, totalPages]);

    // Handle hover prefetch - start loading page when user hovers
    const handlePrefetchPage = useCallback((pageNum) => {
        if (prefetchPage) {
            prefetchPage(pageNum, category, region, date, past);
        }
    }, [prefetchPage, category, region, date, past]);

    // Render pagination
    const renderPagination = () => {
        if (!auctions || !auctions.length) return null;

        // Use totalPages from API if available, otherwise fall back to old heuristic
        const isLastPage = totalPages
            ? pageNumber >= totalPages
            : auctions.length < auctionsPerPage;

        return (
            <PaginationWrapper
                currentPage={page}
                getUrl={(p) => getUrl(p, category, region, date)}
                hideNexts={isLastPage}
                totalPages={totalPages}
                loading={auctionsLoading}
                onPrefetch={handlePrefetchPage}
            />
        );
    };

    // Show loading only when no data exists (initial load)
    const showInitialLoading = auctionsLoading && (!auctions || !auctions.length);

    // Determine content to show
    let content;

    if (showInitialLoading) {
        content = (
            <div className="al-auctions-loading">
                <ProgressIndicator text="Loading auctions..." />
            </div>
        );
    } else if (auctions && auctions.length) {
        content = (
            <div className={`al-auctions-grid ${auctionsLoading ? 'al-auctions-grid--loading' : ''}`}>
                {auctions.map(auction => (
                    <AuctionPreview key={auction.id} auction={auction} />
                ))}
            </div>
        );
    } else if (pageNumber && pageNumber > 1) {
        // Past last page, redirect to page 1
        return <Navigate to={getUrl(1, category, region, date)} replace />;
    } else {
        content = (
            <div className="al-auctions-empty">
                <p>No auctions available</p>
                {pageNumber > 1 && (
                    <Link to={getUrl(1)} className="al-link">
                        Go back to first page
                    </Link>
                )}
            </div>
        );
    }

    return (
        <div className="al-auctions-list">
            {/* Header with title and filters */}
            {!showcase && (
                <header className="al-auctions-header">
                    <div className="al-auctions-title">
                        {!lockCategory && (
                            <h1>
                                <Link to={getUrl()}>
                                    {past ? 'Past Auctions' : categoryName
                                        ? `Upcoming ${categoryName.replace(/\s+[Aa]uctions?$/, '')} Auctions`
                                        : 'Upcoming Auctions'}
                                </Link>
                            </h1>
                        )}
                    </div>
                    <div className="al-auctions-filters">
                        <Filter past={past} />
                    </div>
                </header>
            )}

            {/* Pagination top */}
            {renderPagination()}

            {/* Main content */}
            {content}

            {/* Pagination bottom */}
            {renderPagination()}
        </div>
    );
};

AuctionsList.propTypes = {
    loadData: PropTypes.func,
    prefetchPage: PropTypes.func,
    auctions: PropTypes.arrayOf(AUCTION_PROP_TYPE),
    filterRegion: PropTypes.string,
    filterCategory: PropTypes.string,
    filterDate: PropTypes.string,
    auctionsLoading: PropTypes.bool,
    past: PropTypes.bool,
    categoryName: PropTypes.string,
    lockCategory: PropTypes.string,
    auctionsPerPage: PropTypes.number.isRequired,
    getUrl: PropTypes.func.isRequired,
    totalPages: PropTypes.number,
    totalItems: PropTypes.number,
    premiumFilter: PropTypes.string,
    Filter: PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.func
    ])
};

export default AuctionsList;
